import type { LinkableDlnItemsLookupParams } from './types';

import {
  infiniteQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { ms } from 'ms';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { queryKeysInvoice } from '../queryKeys';

import { fetchLinkableDlnItems } from './fetchLinkableDlnItems';
import { selectLinkableDlnItemPages } from './selectLinkableDlnItemPages';

/**
 * Generates infinite query options for the delivery note lines a line item
 * can be linked to, one page per offset. The selected data is the flat list
 * of every loaded line plus the counts over all matching lines. A failed
 * page stays in `error`; nothing is toasted.
 *
 * @param params - Invoice, line item and filters.
 * @returns Infinite query options.
 */
function getLinkableDlnItemsInfiniteQueryOptions(
  params: LinkableDlnItemsLookupParams,
) {
  return infiniteQueryOptions({
    queryFn: ({ pageParam, signal }) =>
      fetchLinkableDlnItems(
        { ...params, limit: PAGINATION_PAGE_SIZE_DEFAULT, offset: pageParam },
        { signal },
      ),
    initialPageParam: 0,
    meta: { suppressError: true },
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, pages) =>
      lastPage.hasNextPage
        ? pages.length * PAGINATION_PAGE_SIZE_DEFAULT
        : undefined,
    queryKey: queryKeysInvoice.getLinkableDlnItems(params),
    select: selectLinkableDlnItemPages,
    staleTime: ms('30s'),
    throwOnError: false,
  });
}

/**
 * Loads the linkable delivery note lines of a line item page by page.
 *
 * @param params - Invoice, line item and filters.
 * @returns TanStack infinite query result.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/search_linkable_dln_items_invoices__invoice_id__line_items__line_item_id__linkable_dln_items_get
 */
export function useInfiniteQueryLinkableDlnItems(
  params: LinkableDlnItemsLookupParams,
) {
  return useInfiniteQuery(getLinkableDlnItemsInfiniteQueryOptions(params));
}
